import * as THREE from "three";
import type { FloorPlan3DMaterialSlot } from "../../../materials/materialResolver";
import type { LivingRoom3DPart } from "./livingRoomGeometry";

type EllipsoidPart = Extract<LivingRoom3DPart, { type: "ellipsoid" }>;
type ProfilePart = Extract<LivingRoom3DPart, { type: "bonnieProfile" }>;
type ProfilePoint = ProfilePart["profile"][number];

/** One neutral material keeps Bonnie consistent with the other primitives. */
const BONNIE_MATERIAL: FloorPlan3DMaterialSlot = "furnitureFabric";

const TORSO_PROFILE = [
  // height, half-width, half-depth, depth offset (all normalized)
  // Androgynous silhouette: hips and shoulders nearly the same width, a
  // straight waist and a flat chest. Negative depth offset pushes the
  // section towards the front of the body.
  [0, 0.3, 0.28, 0.01], // lower hip, merges flush with the thighs
  [0.12, 0.345, 0.315, 0.012], // hip
  [0.24, 0.325, 0.28, 0.008], // iliac crest
  [0.38, 0.29, 0.245, 0.005], // waist
  [0.52, 0.3, 0.245, 0], // ribcage
  [0.66, 0.315, 0.26, -0.01], // chest
  [0.78, 0.305, 0.24, -0.005], // upper chest
  [0.89, 0.355, 0.23, 0], // shoulder line
  [0.96, 0.235, 0.18, 0], // trapezius taper
  [1, 0.115, 0.15, 0], // neck base
] as const;

/**
 * Builds Bonnie as a smooth, relaxed standing figure with an androgynous,
 * sex-neutral silhouette. The arms and legs are continuous tapered
 * surfaces, the shoulders are part of the torso and a short bob covers the
 * back of the head. Every coordinate is proportional, so the authored
 * height remains exact.
 */
export function createBonnieParts(
  width: number,
  depth: number,
  height: number,
  baseElevation: number,
): LivingRoom3DPart[] {
  const z = (ratio: number) => baseElevation + height * ratio;
  const ellipsoid = (
    id: string,
    center: [number, number, number],
    radii: [number, number, number],
  ): EllipsoidPart => ({
    id,
    type: "ellipsoid",
    center,
    radiusX: radii[0],
    radiusY: radii[1],
    radiusZ: radii[2],
    widthSegments: 48,
    heightSegments: 32,
    materialSlot: BONNIE_MATERIAL,
    castShadow: false,
    receiveShadow: true,
  });
  const profile = (
    id: string,
    points: ProfilePoint[],
    verticalSegments = 40,
    capStart = true,
    capEnd = true,
  ): ProfilePart => ({
    id,
    type: "bonnieProfile",
    center: [0, 0, 0],
    profile: points,
    radialSegments: 48,
    verticalSegments,
    capStart,
    capEnd,
    materialSlot: BONNIE_MATERIAL,
    castShadow: false,
    receiveShadow: true,
  });
  const mirroredProfile = (
    id: string,
    side: -1 | 1,
    points: Array<{
      x: number;
      y: number;
      z: number;
      radiusX: number;
      radiusY: number;
    }>,
    capStart = false,
    capEnd = false,
  ) =>
    profile(
      id,
      points.map((point) => ({
        center: [side * width * point.x, depth * point.y, z(point.z)],
        radiusX: width * point.radiusX,
        radiusY: depth * point.radiusY,
      })),
      40,
      capStart,
      capEnd,
    );

  const legProfile = [
    { x: 0.15, y: -0.02, z: 0.03, radiusX: 0.008, radiusY: 0.02 },
    { x: 0.15, y: 0, z: 0.055, radiusX: 0.062, radiusY: 0.12 }, // slim ankle
    { x: 0.15, y: 0.005, z: 0.11, radiusX: 0.075, radiusY: 0.155 },
    { x: 0.15, y: 0.012, z: 0.19, radiusX: 0.105, radiusY: 0.2 }, // calf
    { x: 0.15, y: 0.015, z: 0.26, radiusX: 0.082, radiusY: 0.16 }, // below knee
    { x: 0.15, y: 0.018, z: 0.31, radiusX: 0.09, radiusY: 0.175 }, // knee
    { x: 0.15, y: 0.022, z: 0.4, radiusX: 0.12, radiusY: 0.21 }, // mid thigh
    { x: 0.155, y: 0.026, z: 0.49, radiusX: 0.14, radiusY: 0.225 }, // upper thigh
    { x: 0.16, y: 0.026, z: 0.53, radiusX: 0.15, radiusY: 0.235 },
    { x: 0.16, y: 0.026, z: 0.56, radiusX: 0.01, radiusY: 0.025 },
  ];
  const armProfile = [
    { x: 0.3, y: -0.03, z: 0.415, radiusX: 0.008, radiusY: 0.018 },
    { x: 0.3, y: -0.03, z: 0.425, radiusX: 0.03, radiusY: 0.03 },
    { x: 0.3, y: -0.03, z: 0.45, radiusX: 0.068, radiusY: 0.05 }, // hand
    { x: 0.305, y: -0.028, z: 0.485, radiusX: 0.06, radiusY: 0.04 },
    { x: 0.315, y: -0.022, z: 0.52, radiusX: 0.048, radiusY: 0.078 }, // wrist
    { x: 0.33, y: -0.01, z: 0.58, radiusX: 0.055, radiusY: 0.095 }, // forearm
    { x: 0.345, y: 0.005, z: 0.645, radiusX: 0.048, radiusY: 0.085 }, // elbow
    { x: 0.355, y: 0.02, z: 0.71, radiusX: 0.062, radiusY: 0.105 }, // upper arm
    { x: 0.36, y: 0.032, z: 0.755, radiusX: 0.068, radiusY: 0.11 }, // deltoid
    { x: 0.35, y: 0.04, z: 0.785, radiusX: 0.05, radiusY: 0.085 },
    { x: 0.34, y: 0.045, z: 0.8, radiusX: 0.008, radiusY: 0.02 },
  ];
  return [
    // Soft shoes touch the floor and share the same finish as the body.
    ellipsoid(
      "shoe-left",
      [-width * 0.15, -depth * 0.12, z(0.028)],
      [width * 0.09, depth * 0.32, height * 0.028],
    ),
    ellipsoid(
      "shoe-right",
      [width * 0.15, -depth * 0.12, z(0.028)],
      [width * 0.09, depth * 0.32, height * 0.028],
    ),
    mirroredProfile("leg-left", -1, legProfile, true, true),
    mirroredProfile("leg-right", 1, legProfile, true, true),

    // The hip, waist, bust and shoulders are one uninterrupted surface.
    {
      id: "torso",
      type: "bonnieTorso",
      center: [0, 0, z(0.48)],
      width,
      depth,
      height: height * 0.36,
      materialSlot: BONNIE_MATERIAL,
      castShadow: false,
      receiveShadow: true,
    },
    mirroredProfile("arm-left", -1, armProfile, true, true),
    mirroredProfile("arm-right", 1, armProfile, true, true),

    profile(
      "head",
      [
        {
          center: [0, 0, z(0.835)],
          radiusX: width * 0.09,
          radiusY: depth * 0.15,
        },
        {
          center: [0, -depth * 0.005, z(0.852)],
          radiusX: width * 0.082,
          radiusY: depth * 0.14,
        },
        {
          center: [0, -depth * 0.012, z(0.868)],
          radiusX: width * 0.085,
          radiusY: depth * 0.15,
        },
        {
          center: [0, -depth * 0.024, z(0.884)],
          radiusX: width * 0.105,
          radiusY: depth * 0.2,
        },
        {
          center: [0, -depth * 0.036, z(0.905)],
          radiusX: width * 0.135,
          radiusY: depth * 0.26,
        },
        {
          center: [0, -depth * 0.042, z(0.928)],
          radiusX: width * 0.15,
          radiusY: depth * 0.295,
        },
        {
          center: [0, -depth * 0.036, z(0.95)],
          radiusX: width * 0.145,
          radiusY: depth * 0.285,
        },
        {
          center: [0, -depth * 0.024, z(0.97)],
          radiusX: width * 0.125,
          radiusY: depth * 0.245,
        },
        {
          center: [0, -depth * 0.012, z(0.985)],
          radiusX: width * 0.09,
          radiusY: depth * 0.185,
        },
        {
          center: [0, -depth * 0.004, z(0.995)],
          radiusX: width * 0.045,
          radiusY: depth * 0.11,
        },
        {
          center: [0, 0, z(1)],
          radiusX: width * 0.015,
          radiusY: depth * 0.05,
        },
      ],
      48,
      true,
      true,
    ),

    // A short, neutral bob covers the back of the head and ends at the
    // nape, leaving the face open. Like every other profile it is authored
    // bottom-up so the generated triangle winding keeps the normals
    // facing outward.
    profile(
      "hair",
      [
        {
          center: [0, depth * 0.065, z(0.855)],
          radiusX: width * 0.05,
          radiusY: depth * 0.06,
        },
        {
          center: [0, depth * 0.05, z(0.875)],
          radiusX: width * 0.16,
          radiusY: depth * 0.2,
        },
        {
          center: [0, depth * 0.032, z(0.9)],
          radiusX: width * 0.165,
          radiusY: depth * 0.24,
        },
        {
          center: [0, depth * 0.012, z(0.928)],
          radiusX: width * 0.17,
          radiusY: depth * 0.29,
        },
        {
          center: [0, depth * 0.004, z(0.95)],
          radiusX: width * 0.165,
          radiusY: depth * 0.285,
        },
        {
          center: [0, 0, z(0.97)],
          radiusX: width * 0.145,
          radiusY: depth * 0.255,
        },
        {
          center: [0, -depth * 0.004, z(0.985)],
          radiusX: width * 0.105,
          radiusY: depth * 0.2,
        },
        {
          center: [0, -depth * 0.006, z(0.992)],
          radiusX: width * 0.075,
          radiusY: depth * 0.15,
        },
        {
          center: [0, -depth * 0.002, z(0.997)],
          radiusX: width * 0.04,
          radiusY: depth * 0.09,
        },
        {
          center: [0, 0, z(1)],
          radiusX: width * 0.02,
          radiusY: depth * 0.06,
        },
      ],
      48,
      true,
      true,
    ),
  ];
}

/** Smooth elliptical torso with an integrated human shoulder line. */
export function createBonnieTorsoBufferGeometry(
  width: number,
  depth: number,
  height: number,
): THREE.BufferGeometry {
  const points: ProfilePoint[] = TORSO_PROFILE.map(
    ([profileZ, radiusX, radiusY, offsetY]) => ({
      center: [0, offsetY * depth, profileZ * height],
      radiusX: radiusX * width,
      radiusY: radiusY * depth,
    }),
  );
  return createBonnieProfileBufferGeometry(points, 48, 40, true, true);
}

/**
 * Generates a continuous, capped elliptical surface along a curved centreline.
 * It replaces visible ball joints with a single soft silhouette.
 */
export function createBonnieProfileBufferGeometry(
  profile: ProfilePoint[],
  radialSegments = 48,
  verticalSegments = 40,
  capStart = true,
  capEnd = true,
): THREE.BufferGeometry {
  const centerCurve = new THREE.CatmullRomCurve3(
    profile.map(({ center }) => new THREE.Vector3(...center)),
    false,
    "centripetal",
  );
  const radiusCurve = new THREE.CatmullRomCurve3(
    profile.map(
      ({ radiusX, radiusY }) => new THREE.Vector3(radiusX, radiusY, 0),
    ),
    false,
    "centripetal",
  );
  const vertices: number[] = [];
  const indices: number[] = [];

  for (let row = 0; row <= verticalSegments; row += 1) {
    const t = row / verticalSegments;
    const center = centerCurve.getPoint(t);
    const radius = radiusCurve.getPoint(t);
    for (let column = 0; column <= radialSegments; column += 1) {
      const angle = (column / radialSegments) * Math.PI * 2;
      vertices.push(
        center.x + Math.cos(angle) * Math.max(radius.x, 0.001),
        center.y + Math.sin(angle) * Math.max(radius.y, 0.001),
        center.z,
      );
    }
  }

  const rowSize = radialSegments + 1;
  for (let row = 0; row < verticalSegments; row += 1) {
    for (let column = 0; column < radialSegments; column += 1) {
      const a = row * rowSize + column;
      const b = a + rowSize;
      indices.push(a, a + 1, b, b, a + 1, b + 1);
    }
  }

  if (capStart) {
    const bottomCenterIndex = vertices.length / 3;
    vertices.push(...profile[0].center);
    for (let column = 0; column < radialSegments; column += 1) {
      indices.push(bottomCenterIndex, column + 1, column);
    }
  }
  if (capEnd) {
    const topCenterIndex = vertices.length / 3;
    vertices.push(...profile[profile.length - 1].center);
    const topRowOffset = verticalSegments * rowSize;
    for (let column = 0; column < radialSegments; column += 1) {
      indices.push(
        topCenterIndex,
        topRowOffset + column,
        topRowOffset + column + 1,
      );
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(vertices, 3),
  );
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}
